import { OptionalProductAdjustQuantityType } from 'types/productPackage';

/**
 * normalizeProductQuantities
 * This utility function takes an array of products and normalizes their quantities for backend processing.
 * It ensures that each product is represented individually with a single quantity, including handling shipping countries.
 * For products with shipping countries, it assigns a shipping country to each individual product.
 * For digital or non-shippable products, it simply returns the quantity as specified.
 *
 * @param {OptionalProductAdjustQuantityType[]} products - Array of product objects to normalize.
 * @returns {Array} - An array of normalized product objects.
 */
export function normalizeProductQuantities(products: OptionalProductAdjustQuantityType[]) {
  const transformedProducts = products.flatMap((product) => {
    const totalQuantity = product.quantity;

    // If the product has shippingCountries, we map through them
    if (product.shippingCountries.length) {
      const expandedShippingCountries = [
        ...product.shippingCountries,
        ...Array(Math.max(0, totalQuantity - product.shippingCountries.length)).fill('NO'),
      ];

      return Array.from({ length: totalQuantity }, (_, index) => ({
        productId: product.productId,
        quantity: 1, // Each entry represents a single quantity
        shippingCountry: expandedShippingCountries[index],
      }));
    }

    // For digital products or products without shipping countries, just return quantities
    return {
      productId: product.productId,
      quantity: product.quantity,
    };
  });

  return transformedProducts;
}
